/**
 * EntrepreneurTileContent — Batch #14.313 / #14.314
 * Contenu des tuiles Entreprise du hub Tenue de livres : apercu (grand livre + réglages) · taxes (TPS/TVQ par période).
 * Les anciennes tuiles revenus / depenses / kilometrage redirigent vers Files PMS › Entreprise.
 */
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, ENT_INBOX_REDIRECT, YearSelect } from "./_shared";
import OverviewTile from "./OverviewTile";
import TaxesTile from "./TaxesTile";

export default function EntrepreneurTileContent({ masterId, tileId }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/entrepreneurs/${masterId}/overview?year=${year}`, { headers: authH() });
      setOverview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement dossier");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [masterId, year]);

  useEffect(() => { load(); }, [load]);

  if (ENT_INBOX_REDIRECT[tileId]) return <Navigate to={`/admin/pms-queue?tab=${ENT_INBOX_REDIRECT[tileId]}`} replace />;

  if (!loading && !overview) {
    return (
      <div className="bg-white border-2 border-rose-200 rounded-xl p-8 text-center" data-testid="ent-tile-error">
        <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
        <h2 className="text-base font-bold text-slate-800">Dossier inaccessible</h2>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`ent-tile-${tileId}`}>
      <div className="flex items-center justify-end gap-2">
        <span className="text-[11px] font-semibold text-slate-500 uppercase">Année</span>
        <YearSelect value={year} onChange={setYear} testId="ent-year-select" />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>
      {overview && (
        <>
          {tileId === "apercu" && <OverviewTile masterId={masterId} overview={overview} onChanged={load} />}
          {tileId === "taxes" && <TaxesTile masterId={masterId} year={year} />}
        </>
      )}
    </div>
  );
}
